import { loadStripe, Stripe } from "@stripe/stripe-js";
import { defineMessages } from "react-intl";
import { all, put, select, takeEvery } from "redux-saga/effects";

import Organization from "@mapmycustomers/shared/types/Organization";
import Plan from "@mapmycustomers/shared/types/Plan";
import User from "@mapmycustomers/shared/types/User";
import ListResponse from "@mapmycustomers/shared/types/viewModel/ListResponse";

import configService from "@app/config/ConfigService";
import { callApi } from "@app/store/api/callApi";
import { handleError } from "@app/store/errors/actions";
import { getMe, getOrganization } from "@app/store/iam";
import { fetchMe } from "@app/store/iam/actions";
import { getUsers } from "@app/store/members";
import loggingService from "@app/util/logging";

import billingAnalytics from "../billingAnalytics";
import { planNameToInfo } from "../components/plans/planMessages";
import buildCurrentPlan from "../util/buildCurrentPlan";
import { convertToTiers } from "../util/tier";

import { initialize, upgrade } from "./actions";

// Keyed by the server's validation code. v1191 covers sales-managed orgs and self-serve orgs whose
// Stripe subscription the server cannot adopt, so the wording must fit both.
export const upgradeErrorMessages = defineMessages({
  v1191: {
    id: "billing.upgrade.error.salesManaged",
    defaultMessage: "To add a license to your account, please contact Customer Success.",
    description:
      "Error shown when the server refuses self-serve checkout, either because the organization's billing is handled by our team or because its existing Stripe subscription cannot be adopted",
  },
});

export function* onInitialize() {
  try {
    const [stripe, plansResponse]: [Stripe, ListResponse<Plan>] = yield all([
      loadStripe(configService.getStripeCheckoutKey()),
      callApi("fetchPlans"),
    ]);
    const tiers = convertToTiers(plansResponse.data);

    // Mirror the grid's own filter, or this fires on Road Warrior, which is purchasable but
    // individual-only and so never rendered here.
    const unknownPlans = tiers.filter(
      ({ isTrial, name, type }) => !isTrial && type === "team" && !(name in planNameToInfo)
    );
    if (unknownPlans.length) {
      loggingService.error("Billing page has purchasable plans web does not know", {
        planNames: unknownPlans.map(({ name }) => name),
      });
    }

    const currentPlan = buildCurrentPlan(yield select(getMe));

    // offer +1 license by default
    let licenses = currentPlan.licenses + 1;
    // for trial plans offer a number of licenses not less than the number of their users
    if (currentPlan.tier.isTrial) {
      const users: User[] = yield select(getUsers);
      licenses = users.length;
    }

    const addOns = plansResponse.data.filter(({ planType }) => planType === "addOn");

    yield put(initialize.success({ addOns, currentPlan, licenses, stripe, tiers }));
  } catch (error) {
    yield put(initialize.failure(error));
    yield put(handleError({ error }));
  }
}

export function* onUpgrade({ payload }: ReturnType<typeof upgrade.request>) {
  try {
    const org: Organization = yield select(getOrganization);
    const { licenses, planId, token } = payload;
    yield callApi("upgradeSubscription", org.id, token.id, planId, licenses);
    yield put(fetchMe.request()); // update current user's data
    yield put(upgrade.success({}));
    billingAnalytics.completed("Purchase");
  } catch (error) {
    yield put(upgrade.failure(error));
    yield put(handleError({ error, messageByErrorCodeMap: upgradeErrorMessages }));
    billingAnalytics.failed("Purchase");
  }
}

export function* billingSaga() {
  yield takeEvery(initialize.request, onInitialize);
  yield takeEvery(upgrade.request, onUpgrade);
}
